import { useState, useRef, useMemo, Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fmtDate } from "@/lib/locale";
import { InlineSoldDateCell } from "@/components/inline-sold-date-cell";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AccessRestricted } from "@/components/access-restricted";
import { apiRequest } from "@/lib/queryClient";
import { parseMoneyStrict } from "@/lib/moneyInput";
import { FormattedNumberInput } from "@/components/formatted-number-input";
import { type Lender } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { uploadFileDirect } from "@/lib/directUpload";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  Car, Upload, FileText, CheckCircle2, AlertTriangle,
  Clock, DollarSign, TrendingUp, ChevronDown, ChevronRight, Bell, Plus, X, Loader2, Trash2, Sparkles, RefreshCw, ShoppingBag,
  Search, ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import { useLocation } from "wouter";
import { AgedInventorySection } from "@/components/aged-inventory-section";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EnrichedInventoryItem {
  id: string;
  userId: string;
  vin: string | null;
  stockNumber: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  purchasePrice: string | null;
  purchaseDate: string | null;
  salePrice: string | null;
  saleDate: string | null;
  daysInStock: number | null;
  status: string;
  lenderId: string | null;
  lenderName: string | null;
  fundingType: string | null;
  source: string | null;
  costToCarryOverride: string | null;
  reconCost: string | null;
  // Enriched
  daysOnLot: number;
  costToCarry: number;
  matchedLenderId: string | null;
  matchedLenderName: string | null;
  carryingCostPerCarPerDay: number;
  // Floorplan cost breakdown
  estimatedFloorplanInterest: number;
  estimatedFloorplanFees: number;
  effectiveAprUsed: number | null;
  floorplanCostMethod: "estimated" | "accrued" | "none";
}

interface EnrichedResponse {
  items: EnrichedInventoryItem[];
  lenders: Lender[];
  carryingCostPerCarPerDay: number;
  avgReconCost: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined, dec = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: dec });
}

function fmtNum(v: number | null | undefined, dec = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { maximumFractionDigits: dec });
}

function getDaysColor(days: number): string {
  if (days > 90) return "text-destructive font-bold";
  if (days > 60) return "text-amber-600 dark:text-amber-400 font-semibold";
  if (days > 30) return "text-yellow-600 dark:text-yellow-400";
  return "text-emerald-600 dark:text-emerald-400";
}

function getProfitColor(profit: number | null): string {
  if (profit == null) return "text-muted-foreground";
  if (profit > 0) return "text-emerald-600 dark:text-emerald-400";
  return "text-destructive";
}

// Module-level Set to track VIN decode attempts per page session (suppress repeated failures)
const _vinDecodeAttempted = new Set<string>();

// ─── Days Editor (inline) ─────────────────────────────────────────────────────

function computeLiveDays(purchaseDate: string | null): number {
  if (!purchaseDate) return 0;
  const acq = new Date(purchaseDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  acq.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - acq.getTime()) / 86400000));
}

// Days value used for sorting active rows — mirrors the logic DaysCell displays.
function activeDays(item: EnrichedInventoryItem): number {
  return item.source === "receivable" ? (item.daysOnLot ?? 0) : computeLiveDays(item.purchaseDate);
}

// Resolves an item's funding into { owned, label } so the Floored/Owned column can
// be sorted: owned rows always float to the top, floored rows sort by lender name.
function fundingSort(
  item: EnrichedInventoryItem,
  lenderNameById: Map<string, string>
): { owned: boolean; label: string } {
  if (item.fundingType === "owned") return { owned: true, label: "owned" };
  if (item.lenderId) return { owned: false, label: lenderNameById.get(item.lenderId) ?? item.lenderName ?? "floored" };
  if (item.matchedLenderId && item.fundingType !== "owned")
    return { owned: false, label: lenderNameById.get(item.matchedLenderId) ?? item.matchedLenderName ?? "floored" };
  if (item.fundingType === "floored") return { owned: false, label: "floored" };
  return { owned: true, label: "owned" };
}

function DaysCell({ item }: { item: EnrichedInventoryItem }) {
  const { toast } = useToast();
  const { t } = useTranslation(["inventory", "common"]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [dateVal, setDateVal] = useState(item.purchaseDate ?? "");
  // For active receivable items, use server-computed daysOnLot (daysOutstandingAtStmt + daysSinceStmt)
  // because purchaseDate is not populated from floorplan statements.
  // For all other active items, compute live from purchaseDate.
  const liveDays = item.status === "active"
    ? (item.source === "receivable" ? (item.daysOnLot ?? 0) : computeLiveDays(item.purchaseDate))
    : (item.daysOnLot ?? 0);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", `/api/inventory/${item.id}/acquisition-date`, { acquisitionDate: dateVal }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      setEditing(false);
      toast({ title: t("inventory:toast.acquisitionDateUpdated") });
    },
    onError: (err: any) => toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" }),
  });

  if (editing) {
    return (
      <div className="flex items-center gap-1 min-w-[160px]">
        <label className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">{t("inventory:days.purchaseDate")}</label>
        <Input
          type="date"
          value={dateVal}
          onChange={(e) => setDateVal(e.target.value)}
          className="h-7 text-xs w-36"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && dateVal) saveMutation.mutate();
            if (e.key === "Escape") setEditing(false);
          }}
          data-testid={`input-acq-date-${item.id}`}
        />
        <Button size="sm" className="h-7 px-2 text-xs" onClick={() => { if (dateVal) saveMutation.mutate(); }} disabled={!dateVal || saveMutation.isPending} data-testid={`button-save-acq-date-${item.id}`}>
          {saveMutation.isPending ? "…" : t("inventory:actions.save")}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-1" onClick={() => setEditing(false)} data-testid={`button-cancel-acq-date-${item.id}`}>✕</Button>
      </div>
    );
  }

  return (
    <button
      onClick={() => { setDateVal(item.purchaseDate ?? ""); setEditing(true); }}
      className={`text-xs font-semibold tabular-nums hover:underline cursor-pointer ${getDaysColor(liveDays)}`}
      title={item.purchaseDate ? t("inventory:days.acquisitionTitle", { date: item.purchaseDate }) : t("inventory:days.setAcquisitionTitle")}
      data-testid={`text-days-${item.id}`}
    >
      {liveDays}d
    </button>
  );
}

// ─── Vehicle Identity Cell (inline editor for make / model / year / vin) ──────

function IdentityTextCell({
  item,
  field,
  value,
  placeholder,
  monospace,
}: {
  item: EnrichedInventoryItem;
  field: "make" | "model" | "year" | "vin";
  value: string | number | null;
  placeholder?: string;
  monospace?: boolean;
}) {
  const { toast } = useToast();
  const { t } = useTranslation(["inventory", "common"]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [optimisticValue, setOptimisticValue] = useState<string | number | null | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track mousedown on auxiliary buttons so blur knows not to auto-save
  const suppressBlurRef = useRef(false);

  // Use optimistic value when available, otherwise fall back to the prop
  const displayValue = optimisticValue !== undefined ? optimisticValue : value;

  const saveMutation = useMutation({
    mutationFn: (patch: Record<string, any>) =>
      apiRequest("PATCH", `/api/inventory/${item.id}/identity`, patch),
    onSuccess: (_data, patch) => {
      // Set optimistic value immediately so display updates before query re-fetch
      if (patch[field] !== undefined) setOptimisticValue(patch[field]);
      else if (field === "vin" && patch.vin !== undefined) setOptimisticValue(patch.vin);
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      setEditing(false);
      toast({ title: t("inventory:toast.fieldUpdated", { field: field.charAt(0).toUpperCase() + field.slice(1) }) });
    },
    onError: (err: any) => {
      setOptimisticValue(undefined);
      toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" });
    },
  });

  async function decodeVin(vin: string) {
    const v = vin.trim().toUpperCase();
    if (v.length !== 17) return;
    setDecoding(true);
    try {
      const resp = await fetch(`/api/vin-decode?vin=${encodeURIComponent(v)}`, { credentials: "include" });
      if (!resp.ok) { setDecoding(false); return; }
      const data = await resp.json();
      const patch: Record<string, any> = { vin: v };
      if (data.year) patch.year = parseInt(data.year, 10);
      if (data.make) patch.make = data.make;
      if (data.model) patch.model = data.model;
      saveMutation.mutate(patch);
    } catch {
      toast({ title: t("inventory:toast.vinDecodeFailed"), variant: "destructive" });
    } finally {
      setDecoding(false);
    }
  }

  function startEdit() {
    setInputVal(value != null ? String(value) : "");
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function confirm() {
    const trimmed = inputVal.trim();
    // No-op if unchanged
    const originalStr = value != null ? String(value) : "";
    if (trimmed === originalStr) { setEditing(false); return; }
    if (trimmed === "") {
      saveMutation.mutate({ [field]: null });
      return;
    }
    if (field === "year") {
      const y = parseInt(trimmed, 10);
      if (!Number.isFinite(y) || y < 1900 || y > 2100) {
        toast({ title: t("inventory:toast.invalidYear"), variant: "destructive" });
        return;
      }
      if (y === value) { setEditing(false); return; }
      saveMutation.mutate({ year: y });
    } else if (field === "vin") {
      const cleaned = trimmed.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "");
      if (cleaned.length !== 17) {
        toast({ title: t("inventory:toast.vinMust17"), variant: "destructive" });
        return;
      }
      if (cleaned === value) { setEditing(false); return; }
      saveMutation.mutate({ vin: cleaned });
    } else {
      saveMutation.mutate({ [field]: trimmed });
    }
  }

  if (editing) {
    const isVin = field === "vin";
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {saveMutation.isPending && <span className="text-[10px] text-muted-foreground animate-pulse">{t("inventory:actions.savingInline")}</span>}
        <input
          ref={inputRef}
          type={field === "year" ? "number" : "text"}
          value={inputVal}
          onChange={(e) => {
            const v = isVin
              ? e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "").slice(0, 17)
              : e.target.value;
            setInputVal(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { suppressBlurRef.current = true; confirm(); }
            if (e.key === "Escape") { suppressBlurRef.current = true; setEditing(false); }
          }}
          onBlur={() => {
            if (suppressBlurRef.current) {
              suppressBlurRef.current = false;
              return;
            }
            confirm();
          }}
          placeholder={placeholder}
          maxLength={isVin ? 17 : undefined}
          className={`text-xs border border-primary/40 rounded px-1.5 py-0.5 bg-background outline-none focus:ring-1 focus:ring-primary ${isVin ? "w-36 font-mono uppercase" : field === "year" ? "w-14 tabular-nums" : "w-24"}`}
          data-testid={`input-identity-${field}-${item.id}`}
          autoFocus
        />
        {isVin && (
          <button
            onMouseDown={() => { suppressBlurRef.current = true; }}
            onClick={() => { suppressBlurRef.current = false; decodeVin(inputVal); }}
            disabled={decoding || saveMutation.isPending || inputVal.replace(/[^A-HJ-NPR-Z0-9]/gi, "").length !== 17}
            className="text-[10px] px-1.5 py-0.5 rounded border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={t("inventory:identity.decodeTitle")}
            data-testid={`button-vin-decode-${item.id}`}
          >
            {decoding ? <Loader2 className="w-3 h-3 animate-spin" /> : t("inventory:actions.decode")}
          </button>
        )}
        <button
          onMouseDown={() => { suppressBlurRef.current = true; }}
          onClick={() => { suppressBlurRef.current = false; setEditing(false); }}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title={t("inventory:identity.cancelTitle")}
          data-testid={`button-cancel-identity-${field}-${item.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <span
      onDoubleClick={startEdit}
      className={`text-xs cursor-default select-none text-left ${monospace ? "font-mono" : ""}`}
      title={t("inventory:identity.doubleClickEditTitle", { field })}
      data-testid={`cell-identity-${field}-${item.id}`}
    >
      {displayValue != null && displayValue !== "" ? String(displayValue) : <span className="text-muted-foreground/40">—</span>}
    </span>
  );
}

// ─── Purchase Price Cell (inline editor) ──────────────────────────────────────

function PurchasePriceCell({ item }: { item: EnrichedInventoryItem }) {
  const { toast } = useToast();
  const { t } = useTranslation(["inventory", "common"]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");

  const saveMutation = useMutation({
    mutationFn: (val: number) =>
      apiRequest("PATCH", `/api/inventory/${item.id}/sold-details`, { purchasePrice: val }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      setEditing(false);
      toast({ title: t("inventory:toast.purchasePriceUpdated") });
    },
    onError: (err: any) => toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" }),
  });

  function startEdit() {
    const current = item.purchasePrice ? parseFloat(item.purchasePrice) : 0;
    setInputVal(current > 0 ? String(current) : "");
    setEditing(true);
  }

  function confirm() {
    const num = parseMoneyStrict(inputVal);
    if (!Number.isFinite(num) || num < 0) {
      toast({ title: t("inventory:toast.invalidValue"), variant: "destructive" });
      return;
    }
    saveMutation.mutate(num);
  }

  if (editing) {
    return (
      <div className="flex items-center justify-end gap-1">
        {saveMutation.isPending && <span className="text-[10px] text-muted-foreground animate-pulse">{t("inventory:actions.savingInline")}</span>}
        <FormattedNumberInput
          value={inputVal}
          onValueChange={(raw) => setInputVal(raw)}
          allowNegative={false}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") confirm();
            if (e.key === "Escape") setEditing(false);
          }}
          onFocus={(e) => e.currentTarget.select()}
          className="h-auto w-24 text-right text-xs border border-primary/40 rounded px-1.5 py-0.5 tabular-nums"
          testId={`input-purchase-price-${item.id}`}
          autoFocus
        />
      </div>
    );
  }

  return (
    <button
      onClick={startEdit}
      className="text-xs tabular-nums font-medium hover:underline cursor-pointer hover:text-primary transition-colors"
      title={t("inventory:purchasePrice.editTitle")}
      data-testid={`cell-purchase-price-${item.id}`}
    >
      {item.purchasePrice ? fmt$(parseFloat(item.purchasePrice)) : <span className="text-muted-foreground">—</span>}
    </button>
  );
}

// ─── Cost to Carry Cell (inline editor with override) ─────────────────────────

function CostToCarryCell({ item }: { item: EnrichedInventoryItem }) {
  const { toast } = useToast();
  const { t } = useTranslation(["inventory", "common"]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");

  const hasOverride = item.costToCarryOverride != null;
  const displayValue = item.costToCarry;
  const isFloored = item.floorplanCostMethod !== "none";
  const isEstimated = item.floorplanCostMethod === "estimated";
  const isAccrued = item.floorplanCostMethod === "accrued";
  const overheadCarry = item.daysOnLot * item.carryingCostPerCarPerDay;

  const saveMutation = useMutation({
    mutationFn: (val: number | null) =>
      apiRequest("PATCH", `/api/inventory/${item.id}/sold-details`, { costToCarryOverride: val }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      setEditing(false);
      toast({ title: hasOverride || inputVal === "" ? t("inventory:toast.overrideCleared") : t("inventory:toast.costToCarryUpdated") });
    },
    onError: (err: any) => toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" }),
  });

  function startEdit() {
    const current = hasOverride ? parseFloat(item.costToCarryOverride!) : item.costToCarry;
    setInputVal(current > 0 ? String(Math.round(current)) : "");
    setEditing(true);
  }

  function confirm() {
    const trimmed = inputVal.trim();
    if (trimmed === "") {
      saveMutation.mutate(null);
      return;
    }
    const num = parseMoneyStrict(trimmed);
    if (!Number.isFinite(num) || num < 0) {
      toast({ title: t("inventory:toast.invalidValue"), variant: "destructive" });
      return;
    }
    saveMutation.mutate(num);
  }

  // Build tooltip text for floored vehicles
  function buildBreakdownTitle(): string {
    if (hasOverride) return t("inventory:costToCarry.overrideSet");
    if (isEstimated) {
      const aprStr = item.effectiveAprUsed != null ? t("inventory:costToCarry.trueApr", { pct: item.effectiveAprUsed.toFixed(2) }) : "";
      const lines = [
        t("inventory:costToCarry.estimatedBreakdown"),
        t("inventory:costToCarry.interestLine", { amount: fmt$(item.estimatedFloorplanInterest, 2), apr: aprStr ? t("inventory:costToCarry.aprSuffix", { apr: aprStr }) : "" }),
        item.estimatedFloorplanFees > 0 ? t("inventory:costToCarry.periodFeesLine", { amount: fmt$(item.estimatedFloorplanFees, 2) }) : null,
        overheadCarry > 0 ? t("inventory:costToCarry.overheadLine", { amount: fmt$(overheadCarry, 2) }) : null,
        t("inventory:costToCarry.clickToOverride"),
      ].filter(Boolean);
      return lines.join("\n");
    }
    if (isAccrued) {
      return t("inventory:costToCarry.accruedTitle");
    }
    return t("inventory:costToCarry.setOverrideTitle");
  }

  if (editing) {
    return (
      <div className="flex items-center justify-end gap-1">
        {saveMutation.isPending && <span className="text-[10px] text-muted-foreground animate-pulse">{t("inventory:actions.savingInline")}</span>}
        <FormattedNumberInput
          value={inputVal}
          onValueChange={(raw) => setInputVal(raw)}
          allowNegative={false}
          onBlur={confirm}
          onKeyDown={(e) => {
            if (e.key === "Enter") confirm();
            if (e.key === "Escape") setEditing(false);
          }}
          onFocus={(e) => e.currentTarget.select()}
          placeholder={t("inventory:costToCarry.overrideSet")}
          className="h-auto w-24 text-right text-xs border border-primary/40 rounded px-1.5 py-0.5 tabular-nums"
          testId={`input-cost-carry-${item.id}`}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={startEdit}
          className={`text-xs tabular-nums hover:underline cursor-pointer transition-colors ${hasOverride ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-destructive/80"}`}
          title={buildBreakdownTitle()}
          data-testid={`cell-cost-carry-${item.id}`}
        >
          {fmt$(displayValue)}
        </button>
        {hasOverride && (
          <button
            onClick={() => saveMutation.mutate(null)}
            className="text-muted-foreground hover:text-destructive transition-colors"
            title={t("inventory:costToCarry.clearOverrideTitle")}
            data-testid={`button-clear-carry-override-${item.id}`}
            disabled={saveMutation.isPending}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {!hasOverride && isEstimated && (
        <div className="flex items-center gap-1" data-testid={`breakdown-cost-carry-${item.id}`}>
          <span className="text-[10px] text-muted-foreground/70 tabular-nums">
            {t("inventory:costToCarry.int", { amount: fmt$(item.estimatedFloorplanInterest, 0) })}
            {item.estimatedFloorplanFees > 0 && t("inventory:costToCarry.fees", { amount: fmt$(item.estimatedFloorplanFees, 0) })}
            {overheadCarry > 0 && t("inventory:costToCarry.overhead", { amount: fmt$(overheadCarry, 0) })}
          </span>
        </div>
      )}
      {!hasOverride && isAccrued && (
        <span className="text-[10px] text-amber-600/70 dark:text-amber-400/70" title={t("inventory:costToCarry.accruedNote")} data-testid={`method-accrued-${item.id}`}>
          accrued
        </span>
      )}
      {!hasOverride && !isFloored && (
        <span className="text-[10px] text-muted-foreground/60">(calc)</span>
      )}
    </div>
  );
}

// ─── Add Vehicle Dialog ───────────────────────────────────────────────────────

function AddVehicleDialog({ open, onClose, lenders, existingVins }: { open: boolean; onClose: () => void; lenders: Lender[]; existingVins: Set<string> }) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const [vin, setVin] = useState("");
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [acquisitionDate, setAcquisitionDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [purchasePrice, setPurchasePrice] = useState("");
  const [fundingType, setFundingType] = useState<"owned" | "floored">("owned");
  const [lenderId, setLenderId] = useState<string>("");
  const [decoding, setDecoding] = useState(false);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [isDuplicateVin, setIsDuplicateVin] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [priceFromReceivable, setPriceFromReceivable] = useState(false);
  // Refs for async-safe reads: avoids stale closure values in in-flight requests.
  const priceTouchedRef = useRef(false);
  const activeVinRef = useRef("");

  function resetForm() {
    setVin(""); setYear(""); setMake(""); setModel("");
    setAcquisitionDate(new Date().toISOString().slice(0, 10));
    setPurchasePrice(""); setFundingType("owned"); setLenderId(""); setDecoding(false); setDecodeError(null);
    setIsDuplicateVin(false); setShowDuplicateConfirm(false); setPriceFromReceivable(false);
    priceTouchedRef.current = false;
    activeVinRef.current = "";
  }

  async function decodeVin(rawVin: string) {
    const v = rawVin.trim().toUpperCase();
    if (v.length !== 17) return;
    activeVinRef.current = v;
    setDecoding(true);
    setDecodeError(null);
    try {
      const [nhtsaResult, receivableResult] = await Promise.allSettled([
        fetch(`/api/vin-decode?vin=${encodeURIComponent(v)}`, { credentials: "include" }),
        fetch(`/api/inventory/receivable-lookup?vin=${encodeURIComponent(v)}`, { credentials: "include" }),
      ]);

      // Guard against stale responses — if the user changed the VIN while
      // the request was in-flight, discard these results entirely.
      if (activeVinRef.current !== v) return;

      let nhtsaData: { year?: number; make?: string; model?: string } | null = null;
      if (nhtsaResult.status === "fulfilled") {
        const nhtsaResp = nhtsaResult.value;
        if (!nhtsaResp.ok) {
          const err = await nhtsaResp.json();
          setDecodeError(err.message ?? t("inventory:addVehicle.couldNotDecode"));
        } else {
          nhtsaData = await nhtsaResp.json();
          if (nhtsaData?.year) setYear(String(nhtsaData.year));
          if (nhtsaData?.make) setMake(nhtsaData.make);
          if (nhtsaData?.model) setModel(nhtsaData.model);
        }
      } else {
        setDecodeError(t("inventory:toast.vinDecodeFailed"));
      }

      if (receivableResult.status === "fulfilled" && receivableResult.value.ok) {
        const receivable: { purchasePrice: string | null; make: string | null; model: string | null; year: number | null; lenderName: string | null; fundingType: string | null; statementDate: string | null } | null = await receivableResult.value.json();
        if (receivable) {
          if (receivable.purchasePrice) {
            const priceNum = parseFloat(receivable.purchasePrice);
            // Prefill price when the field is empty OR has not been manually touched.
            // This covers: (a) untouched field, (b) dealer touched then cleared the field.
            setPurchasePrice((currentPrice) => {
              if ((!isNaN(priceNum) && priceNum > 0) && (!priceTouchedRef.current || currentPrice === "")) {
                setPriceFromReceivable(true);
                return fmtInput(String(Math.round(priceNum)));
              }
              return currentPrice;
            });
          }
          if (receivable.year && !nhtsaData?.year) setYear(String(receivable.year));
          if (receivable.make && !nhtsaData?.make) setMake(receivable.make);
          if (receivable.model && !nhtsaData?.model) setModel(receivable.model);
          // Pre-fill funding type, lender, and floor date when the receivable is floored.
          if (receivable.fundingType === "floored") {
            setFundingType("floored");
            if (receivable.lenderName) {
              const normalizedName = receivable.lenderName.trim().toLowerCase();
              const matched = lenders.find((l) => l.name.trim().toLowerCase() === normalizedName);
              if (matched) setLenderId(matched.id);
            }
            // Pre-fill the acquisition date from the statement date (floor date).
            // Only overwrite if the field still holds the default today-value.
            if (receivable.statementDate) {
              const today = new Date().toISOString().slice(0, 10);
              setAcquisitionDate((cur) => (cur === today ? receivable.statementDate! : cur));
            }
          }
        }
      }
      // receivableResult "rejected" is silently ignored — the receivable lookup is
      // a best-effort enhancement and should never break the primary VIN decode.
    } catch {
      if (activeVinRef.current === v) setDecodeError(t("inventory:toast.vinDecodeFailed"));
    } finally {
      if (activeVinRef.current === v) setDecoding(false);
    }
  }

  function handleVinChange(v: string) {
    const cleaned = v.trim().toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "").slice(0, 17);
    setVin(cleaned);
    activeVinRef.current = cleaned;
    // Note: priceTouchedRef is intentionally NOT reset on VIN changes —
    // manual price entry before VIN is complete must still be respected.
    setYear(""); setMake(""); setModel(""); setDecodeError(null); setPriceFromReceivable(false);
    setIsDuplicateVin(cleaned.length === 17 && existingVins.has(cleaned));
    if (cleaned.length === 17) {
      decodeVin(cleaned);
    } else {
      // If VIN drops below 17 chars, immediately clear any in-flight spinner.
      // The in-flight decode's finally block won't clear it (activeVinRef no longer matches).
      setDecoding(false);
    }
  }

  function fmtInput(raw: string): string {
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits) return "";
    return parseInt(digits, 10).toLocaleString("en-US");
  }

  const purchasePriceNum = parseFloat(purchasePrice.replace(/,/g, "")) || 0;
  const canSubmit = vin.length === 17 && purchasePriceNum > 0 && acquisitionDate && (fundingType === "owned" || lenderId);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/inventory", {
        vin,
        year: year ? parseInt(year, 10) : null,
        make: make || null,
        model: model || null,
        purchasePrice: String(purchasePriceNum),
        purchaseDate: acquisitionDate,
        status: "active",
        source: "manual",
        fundingType,
        lenderId: fundingType === "floored" && lenderId ? lenderId : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      toast({ title: t("inventory:toast.vehicleAdded") });
      resetForm();
      onClose();
    },
    onError: (err: any) => toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" }),
  });

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) { resetForm(); onClose(); } }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("inventory:addVehicle.title")}</DialogTitle>
          <DialogDescription>{t("inventory:addVehicle.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("inventory:addVehicle.vinLabel")} <span className="text-muted-foreground">{t("inventory:addVehicle.vinChars")}</span></Label>
            <div className="relative">
              <Input
                value={vin}
                onChange={(e) => handleVinChange(e.target.value)}
                placeholder={t("inventory:addVehicle.vinPlaceholder")}
                className="font-mono uppercase pr-8"
                maxLength={17}
                data-testid="input-add-vehicle-vin"
              />
              {decoding && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </span>
              )}
            </div>
            {decodeError && <p className="text-xs text-destructive">{decodeError}</p>}
            {isDuplicateVin && !decodeError && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1" data-testid="warning-duplicate-vin">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {t("inventory:addVehicle.duplicateVin")}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("inventory:addVehicle.year")}</Label>
              <Input
                value={year}
                onChange={(e) => setYear(e.target.value)}
                placeholder={t("inventory:addVehicle.yearPlaceholder")}
                className={`text-xs ${year && !decodeError ? "bg-muted/40" : ""}`}
                data-testid="input-add-vehicle-year"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("inventory:addVehicle.make")}</Label>
              <Input
                value={make}
                onChange={(e) => setMake(e.target.value)}
                placeholder={t("inventory:addVehicle.makePlaceholder")}
                className={`text-xs ${make && !decodeError ? "bg-muted/40" : ""}`}
                data-testid="input-add-vehicle-make"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("inventory:addVehicle.model")}</Label>
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder={t("inventory:addVehicle.modelPlaceholder")}
                className={`text-xs ${model && !decodeError ? "bg-muted/40" : ""}`}
                data-testid="input-add-vehicle-model"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{t("inventory:addVehicle.acquisitionDate")}</Label>
            <Input
              type="date"
              value={acquisitionDate}
              onChange={(e) => setAcquisitionDate(e.target.value)}
              data-testid="input-add-vehicle-acq-date"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t("inventory:addVehicle.purchasePrice")}</Label>
              {priceFromReceivable && (
                <span className="text-xs text-muted-foreground italic" data-testid="label-price-from-receivable">{t("inventory:addVehicle.fromReceivable")}</span>
              )}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
              <Input
                type="text" inputMode="numeric"
                placeholder={t("inventory:addVehicle.pricePlaceholder")}
                value={purchasePrice}
                onChange={(e) => { priceTouchedRef.current = true; setPurchasePrice(fmtInput(e.target.value)); setPriceFromReceivable(false); }}
                className="pl-6"
                data-testid="input-add-vehicle-price"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{t("inventory:addVehicle.funding")}</Label>
            <Select value={fundingType} onValueChange={(v) => setFundingType(v as "owned" | "floored")}>
              <SelectTrigger data-testid="select-add-vehicle-funding">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owned">{t("inventory:addVehicle.ownedCash")}</SelectItem>
                <SelectItem value="floored">{t("inventory:addVehicle.floored")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {fundingType === "floored" && (
            <div className="space-y-1.5">
              <Label className="text-xs">{t("inventory:addVehicle.lender")}</Label>
              <Select value={lenderId} onValueChange={setLenderId}>
                <SelectTrigger data-testid="select-add-vehicle-lender">
                  <SelectValue placeholder={t("inventory:addVehicle.selectLender")} />
                </SelectTrigger>
                <SelectContent>
                  {lenders.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onClose(); }} data-testid="button-cancel-add-vehicle">{t("inventory:actions.cancel")}</Button>
          <Button
            onClick={() => {
              if (isDuplicateVin) {
                setShowDuplicateConfirm(true);
              } else {
                saveMutation.mutate();
              }
            }}
            disabled={!canSubmit || saveMutation.isPending}
            data-testid="button-add-vehicle-submit"
          >
            {saveMutation.isPending ? t("inventory:actions.adding") : t("inventory:actions.addVehicle")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showDuplicateConfirm} onOpenChange={(o) => { if (!o) setShowDuplicateConfirm(false); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("inventory:addVehicle.duplicateTitle")}</DialogTitle>
          <DialogDescription>
            {t("inventory:addVehicle.duplicateConfirm")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDuplicateConfirm(false)} data-testid="button-cancel-duplicate-vehicle">{t("inventory:actions.cancel")}</Button>
          <Button
            onClick={() => { setShowDuplicateConfirm(false); saveMutation.mutate(); }}
            data-testid="button-confirm-duplicate-vehicle"
          >
            {t("inventory:actions.addAnyway")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ─── Floored/Owned Dropdown ───────────────────────────────────────────────────

const FLOORED_NO_LENDER = "__floored__";

function FlooredOwnedCell({ item, lenders }: { item: EnrichedInventoryItem; lenders: Lender[] }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(["inventory", "common"]);

  // Resolve display value:
  // - Explicitly owned → "owned"
  // - Floored with a specific lender → that lender's id
  // - Floored with no lender (or null fundingType with null lender but matched → matchedLenderId)
  // - Floored with no lender and no match → FLOORED_NO_LENDER sentinel
  // - Null fundingType with no lender/match (CSV upload default) → "owned" display
  const currentVal = (() => {
    if (item.fundingType === "owned") return "owned";
    if (item.lenderId) return item.lenderId;
    if (item.matchedLenderId && item.fundingType !== "owned") return item.matchedLenderId;
    if (item.fundingType === "floored") return FLOORED_NO_LENDER;
    return "owned";
  })();

  const saveMutation = useMutation({
    mutationFn: (val: string) =>
      apiRequest("PATCH", `/api/inventory/${item.id}/lender`, {
        lenderId: (val === "owned" || val === FLOORED_NO_LENDER) ? null : val,
        fundingType: val === "owned" ? "owned" : "floored",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
    },
    onError: (err: any) => toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" }),
  });

  return (
    <Select
      value={currentVal}
      onValueChange={(v) => saveMutation.mutate(v)}
      disabled={saveMutation.isPending}
    >
      <SelectTrigger className="h-7 text-xs w-40" data-testid={`select-lender-${item.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="owned">{t("inventory:funding.owned")}</SelectItem>
        <SelectItem value={FLOORED_NO_LENDER}>{t("inventory:funding.floored")}</SelectItem>
        {lenders.length > 0 && (
          <>
            <div className="px-2 py-1 text-[10px] text-muted-foreground uppercase tracking-wide">{t("inventory:funding.byLender")}</div>
            {lenders.map((l) => (
              <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
            ))}
          </>
        )}
      </SelectContent>
    </Select>
  );
}

// ─── Sell Dialog ───────────────────────────────────────────────────────────────

function SellDialog({ item, open, onClose }: { item: EnrichedInventoryItem | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [salePrice, setSalePrice] = useState("");
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [soldChannel, setSoldChannel] = useState<"marketplace" | "other">("other");

  const salePriceNum = parseFloat(salePrice.replace(/,/g, "")) || 0;
  const purchasePriceNum = item?.purchasePrice ? parseFloat(item.purchasePrice) : 0;
  const profit = salePriceNum > 0 ? salePriceNum - purchasePriceNum - (item?.costToCarry ?? 0) : null;

  const sellMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/inventory/${item!.id}/sell`, { salePrice: salePriceNum, saleDate, soldChannel }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace/my"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace/active-source-ids"] });
      toast({ title: t("inventory:toast.vehicleMarkedSold") });
      setSalePrice("");
      onClose();
    },
    onError: (err: any) => toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" }),
  });

  function handleClose() {
    setSalePrice("");
    setSaleDate(new Date().toISOString().slice(0, 10));
    setSoldChannel("other");
    onClose();
  }

  function fmtInput(raw: string): string {
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits) return "";
    return parseInt(digits, 10).toLocaleString("en-US");
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("inventory:sell.title")}</DialogTitle>
          <DialogDescription>
            {item ? t("inventory:sell.descriptionVehicle", { vehicle: [item.year, item.make, item.model].filter(Boolean).join(" ") || item.vin || t("inventory:sell.vehicleFallback") }) : t("inventory:sell.descriptionFallback")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("inventory:sell.whereSold")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="button-sold-channel-marketplace"
                onClick={() => setSoldChannel("marketplace")}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                  soldChannel === "marketplace"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-muted/30 text-muted-foreground hover:bg-muted/60"
                }`}
              >
                <ShoppingBag className="w-4 h-4" />
                {t("inventory:sell.soldMarketplace")}
              </button>
              <button
                type="button"
                data-testid="button-sold-channel-other"
                onClick={() => setSoldChannel("other")}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                  soldChannel === "other"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-muted/30 text-muted-foreground hover:bg-muted/60"
                }`}
              >
                <DollarSign className="w-4 h-4" />
                {t("inventory:sell.soldElsewhere")}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("inventory:sell.salePrice")}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
              <Input
                type="text" inputMode="numeric"
                placeholder={t("inventory:sell.salePricePlaceholder")}
                value={salePrice}
                onChange={(e) => setSalePrice(fmtInput(e.target.value))}
                className="pl-6"
                data-testid="input-sale-price"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("inventory:sell.saleDate")}</Label>
            <Input
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              data-testid="input-sale-date"
            />
          </div>
          {item && salePriceNum > 0 && (
            <div className="text-xs text-muted-foreground space-y-1 rounded-lg bg-muted/40 p-3">
              <div className="flex justify-between">
                <span>{t("inventory:sell.salePrice")}</span>
                <span className="font-medium">{fmt$(salePriceNum)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("inventory:sell.purchasePrice")}</span>
                <span className="font-medium text-destructive/70">− {fmt$(purchasePriceNum)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t("inventory:sell.costToCarry")}</span>
                <span className="font-medium text-destructive/70">− {fmt$(item.costToCarry)}</span>
              </div>
              <Separator className="my-1" />
              <div className="flex justify-between font-semibold">
                <span>{t("inventory:sell.estNetProfit")}</span>
                <span className={getProfitColor(profit)}>{fmt$(profit)}</span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="button-cancel-sale">{t("inventory:actions.cancel")}</Button>
          <Button
            onClick={() => sellMutation.mutate()}
            disabled={salePriceNum <= 0 || sellMutation.isPending}
            data-testid="button-confirm-sale"
          >
            {sellMutation.isPending ? t("inventory:actions.saving") : t("inventory:sell.confirmSale")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Upload Card ───────────────────────────────────────────────────────────────

type SkippedVehicle = { vin: string; year: number | null; make: string | null; model: string | null };
type UploadResult = { imported: number; skipped: number[]; rowErrors: string[]; skippedVehicles?: SkippedVehicle[] };

function UploadCard({ hasInventory }: { hasInventory: boolean }) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [showSkippedModal, setShowSkippedModal] = useState(false);

  async function handleFile(file: File) {
    const isPdf = file.name.toLowerCase().endsWith(".pdf");
    setUploading(true);
    setUploadingPdf(isPdf);
    setResult(null);
    try {
      let resp: Response;
      if (isPdf) {
        // Direct-to-storage: presign → PUT bytes to object storage → finalize.
        const { uploadId } = await uploadFileDirect(file, "inventory_pdf");
        resp = await fetch("/api/inventory/upload-pdf/finalize", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uploadId }),
          credentials: "include",
        });
      } else {
        const formData = new FormData();
        formData.append("file", file);
        resp = await fetch("/api/inventory/upload", {
          method: "POST",
          body: formData,
          credentials: "include",
        });
      }
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.message ?? t("inventory:upload.failed"));
      }
      const data: UploadResult = await resp.json();
      setResult(data);
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      toast({ title: t("inventory:upload.vehiclesImported", { count: data.imported }) });
      if ((data.skippedVehicles?.length ?? 0) > 0) {
        setShowSkippedModal(true);
      }
    } catch (err: any) {
      toast({ title: t("inventory:upload.failed"), description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
      setUploadingPdf(false);
    }
  }

  const skippedVehicles = result?.skippedVehicles ?? [];

  const skippedModal = (
    <Dialog open={showSkippedModal} onOpenChange={(o) => { if (!o) setShowSkippedModal(false); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("inventory:upload.skippedTitle")}</DialogTitle>
          <DialogDescription>
            {t("inventory:upload.skippedDescription", { count: skippedVehicles.length })}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto space-y-1.5 py-1" data-testid="skipped-vehicles-list">
          {skippedVehicles.map((v, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-1 border-b border-border/40 last:border-0">
              <span className="font-medium">{[v.year, v.make, v.model].filter(Boolean).join(" ") || t("inventory:upload.unknownVehicle")}</span>
              <span className="text-muted-foreground font-mono text-xs ml-auto">{v.vin}</span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={() => setShowSkippedModal(false)} data-testid="button-close-skipped-modal">{t("inventory:actions.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (hasInventory) {
    return (
      <>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outline" size="sm"
            className="gap-1.5"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="button-upload-inventory-pdf"
          >
            <Upload className="w-3.5 h-3.5" />
            {uploading ? (uploadingPdf ? t("inventory:upload.extracting") : t("inventory:upload.uploading")) : t("inventory:upload.uploadPdf")}
          </Button>
          <input
            ref={fileRef} type="file" accept=".csv,.pdf" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleFile(f); }}
            data-testid="input-csv-reupload"
          />
          {result && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              {t("inventory:upload.importedShort", { count: result.imported })}{skippedVehicles.length > 0 ? t("inventory:upload.alreadyOnListSuffix", { count: skippedVehicles.length }) : ""}
            </span>
          )}
          {skippedVehicles.length > 0 && (
            <button
              className="text-xs text-amber-600 dark:text-amber-400 underline underline-offset-2"
              onClick={() => setShowSkippedModal(true)}
              data-testid="button-view-skipped-vehicles"
            >
              {t("inventory:upload.viewSkipped")}
            </button>
          )}
        </div>
        {result && result.rowErrors.length > 0 && (
          <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-2.5 space-y-1 text-xs max-w-md">
            {result.rowErrors.map((e, i) => (
              <p key={i} className="text-amber-700 dark:text-amber-400">{e}</p>
            ))}
          </div>
        )}
        {skippedModal}
      </>
    );
  }

  return (
    <>
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <FileText className="w-10 h-10 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="font-semibold mb-1">{t("inventory:upload.cardTitle")}</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
            {t("inventory:upload.cardDescription")}
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="gap-2"
              data-testid="button-upload-inventory"
            >
              <Upload className="w-4 h-4" />
              {uploading ? (uploadingPdf ? t("inventory:upload.extracting") : t("inventory:upload.uploading")) : t("inventory:upload.uploadList")}
            </Button>
          </div>
          <input
            ref={fileRef} type="file" accept=".csv,.pdf" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleFile(f); }}
            data-testid="input-csv-upload"
          />
          {result && (
            <div className="mt-4 rounded-lg bg-muted/40 p-3 space-y-1 text-xs text-left max-w-xs mx-auto">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {t("inventory:upload.vehiclesImported", { count: result.imported })}
              </div>
              {(result.skipped.length > 0 || skippedVehicles.length > 0) && (
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {skippedVehicles.length > 0
                    ? t("inventory:upload.vehiclesAlreadyOnList", { count: skippedVehicles.length })
                    : t("inventory:upload.rowsSkipped", { count: result.skipped.length })}
                </div>
              )}
              {result.rowErrors.slice(0, 3).map((e, i) => (
                <p key={i} className="text-muted-foreground ml-5">{e}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {skippedModal}
    </>
  );
}

// ─── Alert Settings Dialog ────────────────────────────────────────────────────

function AlertSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [alertEmail, setAlertEmail] = useState("");
  const [alertNotifyByEmail, setAlertNotifyByEmail] = useState(false);
  const [alertCustomAgingDays, setAlertCustomAgingDays] = useState("");

  const { data: profile } = useQuery<Record<string, unknown>>({ queryKey: ["/api/dealer-profile"] });

  // Pre-populate form fields when dialog opens (or when profile loads while open)
  useEffect(() => {
    if (open && profile) {
      setAlertEmail(String(profile.notifyEmail ?? ""));
      setAlertNotifyByEmail(Boolean(profile.notifyByEmail));
      setAlertCustomAgingDays(profile.alertCustomAgingDays != null ? String(profile.alertCustomAgingDays) : "");
    }
  }, [open, profile]);

  const saveMutation = useMutation({
    mutationFn: (data: { notifyByEmail: boolean; notifyEmail: string; alertCustomAgingDays: number | null }) =>
      apiRequest("PATCH", "/api/alerts/preferences", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      toast({ title: t("inventory:toast.alertPreferencesSaved") });
      onClose();
    },
    onError: (err: any) => toast({ title: t("inventory:toast.failedToSave"), description: err.message, variant: "destructive" }),
  });

  function handleSave() {
    const parsedCustom = alertCustomAgingDays.trim() !== "" ? parseInt(alertCustomAgingDays, 10) : null;
    if (parsedCustom !== null && (isNaN(parsedCustom) || parsedCustom < 1)) {
      toast({ title: t("inventory:toast.invalidValue"), description: t("inventory:toast.customAgingMin"), variant: "destructive" });
      return;
    }
    saveMutation.mutate({ notifyByEmail: alertNotifyByEmail, notifyEmail: alertEmail, alertCustomAgingDays: parsedCustom });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("inventory:alerts.title")}</DialogTitle>
          <DialogDescription>{t("inventory:alerts.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="alert-notify-email" className="flex flex-col gap-0.5">
              <span>{t("inventory:alerts.emailNotifications")}</span>
              <span className="text-xs font-normal text-muted-foreground">{t("inventory:alerts.emailNotificationsSubInventory")}</span>
            </Label>
            <Switch
              id="alert-notify-email"
              data-testid="switch-alert-notify-email"
              checked={alertNotifyByEmail}
              onCheckedChange={setAlertNotifyByEmail}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alert-email-input">{t("inventory:alerts.emailAddress")}</Label>
            <Input
              id="alert-email-input"
              data-testid="input-alert-email"
              type="email"
              placeholder={t("inventory:alerts.emailPlaceholder")}
              value={alertEmail}
              onChange={(e) => setAlertEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alert-custom-aging">
              {t("inventory:alerts.customAging")} <span className="text-muted-foreground font-normal">{t("inventory:alerts.optional")}</span>
            </Label>
            <Input
              id="alert-custom-aging"
              data-testid="input-alert-custom-aging"
              type="number"
              min={1}
              placeholder={t("inventory:alerts.customAgingPlaceholder")}
              value={alertCustomAgingDays}
              onChange={(e) => setAlertCustomAgingDays(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t("inventory:alerts.customAgingHelpInventory")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="button-alert-settings-cancel" onClick={onClose}>{t("inventory:actions.cancel")}</Button>
          <Button data-testid="button-alert-settings-save" onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t("inventory:actions.saving") : t("inventory:actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Active Inventory Table ────────────────────────────────────────────────────

function DeleteConfirmDialog({
  item,
  open,
  onClose,
}: {
  item: EnrichedInventoryItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const label = item
    ? [[item.year, item.make, item.model].filter(Boolean).join(" ") || item.vin || t("inventory:delete.thisVehicle")]
        .join("")
    : t("inventory:delete.thisVehicle");

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/inventory/${item!.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      toast({ title: t("inventory:toast.removedFromInventory", { label }) });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: t("inventory:toast.deleteFailed"), description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("inventory:delete.title")}</DialogTitle>
          <DialogDescription>
            {t("inventory:delete.descriptionPrefix")}<strong>{label}</strong>{t("inventory:delete.descriptionSuffix")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending} data-testid="button-delete-cancel">
            {t("inventory:actions.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            data-testid="button-delete-confirm"
          >
            {deleteMutation.isPending ? t("inventory:actions.deleting") : t("inventory:actions.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Inventory Summary Modal ──────────────────────────────────────────────────

function SummaryStatCard({ label, value, sub, testId }: { label: string; value: string; sub?: string; testId: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-border bg-muted/30 px-4 py-3 min-w-0">
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide leading-none">{label}</p>
      <p className="text-xl font-bold tabular-nums leading-tight mt-1" data-testid={`stat-${testId}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground leading-none mt-0.5">{sub}</p>}
    </div>
  );
}

function InventorySummaryModal({
  open,
  onClose,
  ownedItems,
  flooredItems,
}: {
  open: boolean;
  onClose: () => void;
  ownedItems: EnrichedInventoryItem[];
  flooredItems: EnrichedInventoryItem[];
}) {
  const { t } = useTranslation(["inventory", "common"]);
  const [ownedExpanded, setOwnedExpanded] = useState(false);
  const [flooredExpanded, setFlooredExpanded] = useState(false);
  const [insights, setInsights] = useState<string | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const FOCUSABLE = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const container = dialogRef.current;
      if (!container) return;
      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!container.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const ownedTotalValue = ownedItems.reduce((s, i) => s + (parseFloat(i.purchasePrice ?? "0") || 0), 0);
  const ownedAvgDays = ownedItems.length > 0
    ? ownedItems.reduce((s, i) => s + i.daysOnLot, 0) / ownedItems.length
    : 0;
  const ownedTotalCarry = ownedItems.reduce((s, i) => s + i.costToCarry, 0);

  const flooredAvgDays = flooredItems.length > 0
    ? flooredItems.reduce((s, i) => s + i.daysOnLot, 0) / flooredItems.length
    : 0;
  const flooredTotalCost = flooredItems.reduce((s, i) => s + i.costToCarry, 0);

  const lenderBreakdown = flooredItems.reduce<Record<string, number>>((acc, i) => {
    const name = i.matchedLenderName || i.lenderName || t("inventory:summary.unknown");
    acc[name] = (acc[name] ?? 0) + 1;
    return acc;
  }, {});

  const fetchInsights = () => {
    setInsightsLoading(true);
    setInsights(null);
    setInsightsError(null);

    const ownedPayload = ownedItems.map((i) => ({
      purchasePrice: i.purchasePrice,
      daysOnLot: i.daysOnLot,
      costToCarry: i.costToCarry,
    }));
    const flooredPayload = flooredItems.map((i) => ({
      daysOnLot: i.daysOnLot,
      costToCarry: i.costToCarry,
      matchedLenderName: i.matchedLenderName,
      lenderName: i.lenderName,
    }));

    fetch("/api/inventory/summary-insights", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ owned: ownedPayload, floored: flooredPayload }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || t("inventory:summary.failedToLoad"));
        }
        return res.json();
      })
      .then((data) => setInsights(data.insights))
      .catch((err) => setInsightsError(err.message || t("inventory:summary.unableToLoad")))
      .finally(() => setInsightsLoading(false));
  };

  useEffect(() => {
    if (!open) {
      setInsights(null);
      setInsightsError(null);
      setInsightsLoading(false);
      setOwnedExpanded(false);
      setFlooredExpanded(false);
      return;
    }
    fetchInsights();
  }, [open]);

  const bulletPoints = insights
    ? insights.split("\n").filter((l) => l.trim().length > 0)
    : [];

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[49]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }} modal={false}>
        <DialogContent
          ref={dialogRef}
          className="max-w-2xl max-h-[88vh] overflow-y-auto z-50 shadow-2xl"
          data-testid="dialog-inventory-summary"
          aria-modal="true"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="pb-1">
            <DialogTitle className="flex items-center gap-2.5 text-base">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              {t("inventory:summary.modalTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {t("inventory:summary.modalDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-1">
            {/* ── Owned Stats ── */}
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground pl-0.5">{t("inventory:summary.ownedInventory")}</p>
              <div className="grid grid-cols-4 gap-2">
                <SummaryStatCard testId="units" label={t("inventory:summary.units")} value={String(ownedItems.length)} />
                <SummaryStatCard testId="total-value" label={t("inventory:summary.totalValue")} value={fmt$(ownedTotalValue)} />
                <SummaryStatCard testId="avg-days" label={t("inventory:summary.avgDays")} value={ownedAvgDays.toFixed(0)} sub={t("inventory:summary.onLot")} />
                <SummaryStatCard testId="carry-cost" label={t("inventory:summary.carryCost")} value={fmt$(ownedTotalCarry)} />
              </div>
              <div
                className="rounded-xl border border-border overflow-hidden"
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-2.5 cursor-pointer select-none hover:bg-muted/40 transition-colors text-left"
                  onClick={() => setOwnedExpanded((v) => !v)}
                  data-testid="section-owned-inventory"
                >
                  <span className="text-sm font-medium text-muted-foreground">
                    {ownedExpanded ? t("inventory:summary.hideDetail") : t("inventory:summary.showDetail")}
                  </span>
                  {ownedExpanded
                    ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </button>
                {ownedExpanded && (
                  <div className="border-t border-border">
                    {ownedItems.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground">{t("inventory:summary.noOwned")}</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border bg-muted/40">
                            <th className="text-left p-2 pl-4 font-medium text-muted-foreground">{t("inventory:summary.vehicle")}</th>
                            <th className="text-right p-2 font-medium text-muted-foreground">{t("inventory:summary.purchasePrice")}</th>
                            <th className="text-right p-2 font-medium text-muted-foreground">{t("inventory:summary.daysOnLot")}</th>
                            <th className="text-right p-2 pr-4 font-medium text-muted-foreground">{t("inventory:summary.costToCarry")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ownedItems.map((item) => (
                            <tr key={item.id} className="border-b border-border/50 last:border-0" data-testid={`row-owned-${item.id}`}>
                              <td className="p-2 pl-4">
                                {[item.year, item.make, item.model].filter(Boolean).join(" ") || item.stockNumber || t("inventory:summary.unknown")}
                              </td>
                              <td className="p-2 text-right tabular-nums">{fmt$(parseFloat(item.purchasePrice ?? "0") || 0)}</td>
                              <td className={`p-2 text-right tabular-nums ${getDaysColor(item.daysOnLot)}`}>{item.daysOnLot}</td>
                              <td className="p-2 pr-4 text-right tabular-nums">{fmt$(item.costToCarry)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* ── Floored Stats ── */}
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground pl-0.5">{t("inventory:summary.flooredInventory")}</p>
              <div className="grid grid-cols-4 gap-2">
                <SummaryStatCard testId="units" label={t("inventory:summary.units")} value={String(flooredItems.length)} />
                <SummaryStatCard testId="floorplan-cost" label={t("inventory:summary.floorplanCost")} value={fmt$(flooredTotalCost)} />
                <SummaryStatCard testId="avg-days" label={t("inventory:summary.avgDays")} value={flooredAvgDays.toFixed(0)} sub={t("inventory:summary.onLot")} />
                <SummaryStatCard testId="lenders" label={t("inventory:summary.lenders")} value={String(Object.keys(lenderBreakdown).length)} />
              </div>
              {Object.keys(lenderBreakdown).length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-0.5">
                  {Object.entries(lenderBreakdown).map(([name, count]) => (
                    <span key={name} className="inline-flex items-center gap-1 text-xs bg-muted rounded-full px-2.5 py-0.5 text-muted-foreground border border-border">
                      <span className="font-medium text-foreground">{name}</span>
                      <span>·</span>
                      <span>{t("inventory:summary.unitCount", { count })}</span>
                    </span>
                  ))}
                </div>
              )}
              <div className="rounded-xl border border-border overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-2.5 cursor-pointer select-none hover:bg-muted/40 transition-colors text-left"
                  onClick={() => setFlooredExpanded((v) => !v)}
                  data-testid="section-floored-inventory"
                >
                  <span className="text-sm font-medium text-muted-foreground">
                    {flooredExpanded ? t("inventory:summary.hideDetail") : t("inventory:summary.showDetail")}
                  </span>
                  {flooredExpanded
                    ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </button>
                {flooredExpanded && (
                  <div className="border-t border-border">
                    {flooredItems.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground">{t("inventory:summary.noFloored")}</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border bg-muted/40">
                            <th className="text-left p-2 pl-4 font-medium text-muted-foreground">{t("inventory:summary.vehicle")}</th>
                            <th className="text-left p-2 font-medium text-muted-foreground">{t("inventory:summary.lender")}</th>
                            <th className="text-right p-2 font-medium text-muted-foreground">{t("inventory:summary.daysOnLot")}</th>
                            <th className="text-right p-2 pr-4 font-medium text-muted-foreground">{t("inventory:summary.floorplanCost")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {flooredItems.map((item) => (
                            <tr key={item.id} className="border-b border-border/50 last:border-0" data-testid={`row-floored-${item.id}`}>
                              <td className="p-2 pl-4">
                                {[item.year, item.make, item.model].filter(Boolean).join(" ") || item.stockNumber || t("inventory:summary.unknown")}
                              </td>
                              <td className="p-2 text-muted-foreground">{item.matchedLenderName || item.lenderName || "—"}</td>
                              <td className={`p-2 text-right tabular-nums ${getDaysColor(item.daysOnLot)}`}>{item.daysOnLot}</td>
                              <td className="p-2 pr-4 text-right tabular-nums">{fmt$(item.costToCarry)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* ── AI Insights ── */}
            <div
              className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 via-primary/3 to-transparent p-4"
              data-testid="section-ai-insights"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm leading-none">{t("inventory:summary.aiInsights")}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-none">{t("inventory:summary.poweredBy")}</p>
                </div>
                <button
                  type="button"
                  onClick={fetchInsights}
                  disabled={insightsLoading}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title={t("inventory:summary.refreshInsights")}
                  data-testid="button-refresh-insights"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${insightsLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
              {insightsLoading && (
                <div className="flex items-center gap-2.5 text-sm text-muted-foreground py-1" data-testid="insights-loading">
                  <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                  <span>{t("inventory:summary.analyzing")}</span>
                </div>
              )}
              {insightsError && !insightsLoading && (
                <p className="text-sm text-muted-foreground" data-testid="insights-error">{t("inventory:summary.insightsError")}</p>
              )}
              {!insightsLoading && !insightsError && bulletPoints.length > 0 && (
                <ul className="space-y-2.5" data-testid="insights-list">
                  {bulletPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed" data-testid={`insight-bullet-${i}`}>
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
                      <span>{point.replace(/^[•\-]\s*/, "")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={onClose} data-testid="button-close-summary">{t("inventory:actions.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ActiveInventoryTab({ items, lenders, carryingCostPerCarPerDay, isLoading }: {
  items: EnrichedInventoryItem[];
  lenders: Lender[];
  carryingCostPerCarPerDay: number;
  isLoading: boolean;
}) {
  const { t } = useTranslation(["inventory", "common"]);
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [sellItem, setSellItem] = useState<EnrichedInventoryItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<EnrichedInventoryItem | null>(null);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<"days" | "funding" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function toggleSort(key: "days" | "funding") {
    if (sortKey !== key) {
      setSortKey(key);
      // Days defaults to largest-first (surfaces aged units); funding to A→Z.
      setSortDir(key === "days" ? "desc" : "asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  }

  const { data: activeSourceIds } = useQuery<string[]>({
    queryKey: ["/api/marketplace/active-source-ids"],
    staleTime: 30_000,
  });
  const listedInventoryIds = useMemo(() => new Set(activeSourceIds ?? []), [activeSourceIds]);

  const activeItems = useMemo(() => items.filter((i) => i.status === "active"), [items]);
  const activeVinSet = useMemo(() => new Set(activeItems.map((i) => i.vin?.trim().toUpperCase()).filter((v): v is string => !!v)), [activeItems]);

  // Backfill missing year/make/model (and receivable purchase price) for items
  // that came in from a floorplan/receivable upload without identity data.
  // Delegates to the server backfill endpoint, which decodes each VIN via NHTSA
  // with bounded concurrency + retries and persists the result — so a transient
  // decode failure at upload time gets a reliable second chance the next time
  // the dealer opens this page. Guarded so each VIN is only attempted once per
  // session, which keeps undecodable VINs from re-triggering on every render.
  useEffect(() => {
    const missing = activeItems.filter(
      (i) => i.vin && (!i.year || !i.make || !i.model) && !_vinDecodeAttempted.has(i.vin!)
    );
    if (missing.length === 0) return;

    // Mark attempted up front so the request can't double-fire while in flight.
    for (const item of missing) _vinDecodeAttempted.add(item.vin!);

    let cancelled = false;
    (async () => {
      try {
        await apiRequest("POST", "/api/floorplan-inventory/backfill-identity");
        if (!cancelled) qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      } catch {
        // Transient failure (network/500): un-mark so the next page visit retries.
        // Loop-safe because this effect only re-runs when activeItems changes.
        for (const item of missing) _vinDecodeAttempted.delete(item.vin!);
        // Best-effort; per-row manual VIN edit + Decode button remains available.
      }
    })();
    return () => { cancelled = true; };
  }, [activeItems, qc]);

  const totalCapital = useMemo(() => activeItems.reduce((s, i) => s + (parseFloat(i.purchasePrice ?? "0") || 0), 0), [activeItems]);
  const totalCostToCarry = useMemo(() => activeItems.reduce((s, i) => s + i.costToCarry, 0), [activeItems]);
  const ownedItems = useMemo(
    () => activeItems.filter(
      (i) => i.fundingType === "owned" || (!i.fundingType && !i.lenderId && !i.matchedLenderId)
    ),
    [activeItems]
  );
  const flooredItems = useMemo(
    () => activeItems.filter(
      (i) => !(i.fundingType === "owned" || (!i.fundingType && !i.lenderId && !i.matchedLenderId))
    ),
    [activeItems]
  );
  const totalOwnedValue = useMemo(
    () => ownedItems.reduce((s, i) => s + (parseFloat(i.purchasePrice ?? "0") || 0), 0),
    [ownedItems]
  );

  const lenderNameById = useMemo(() => new Map(lenders.map((l) => [l.id, l.name])), [lenders]);

  // Active rows after search filtering + optional column sort.
  const displayedItems = useMemo(() => {
    let list = activeItems;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        [i.year, i.make, i.model, i.vin, i.stockNumber, i.lenderName, i.matchedLenderName]
          .some((f) => f != null && String(f).toLowerCase().includes(q))
      );
    }
    if (sortKey) {
      list = [...list].sort((a, b) => {
        if (sortKey === "days") {
          const diff = activeDays(a) - activeDays(b);
          return sortDir === "asc" ? diff : -diff;
        }
        // funding: owned always on top regardless of direction; floored sorts by lender name.
        const fa = fundingSort(a, lenderNameById);
        const fb = fundingSort(b, lenderNameById);
        if (fa.owned !== fb.owned) return fa.owned ? -1 : 1;
        const cmp = fa.label.localeCompare(fb.label);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return list;
  }, [activeItems, searchQuery, sortKey, sortDir, lenderNameById]);

  const hasInventory = items.length > 0;

  return (
    <div className="space-y-5">
      {/* Upload card / re-upload */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <UploadCard hasInventory={hasInventory} />
        <div className="flex items-center gap-2 shrink-0">
          {activeItems.length > 0 && (
            <Button
              size="sm" variant="outline" className="gap-1.5"
              onClick={() => setShowSummary(true)}
              data-testid="button-inventory-summary"
            >
              <Sparkles className="w-3.5 h-3.5" />{t("inventory:active.summary")}
            </Button>
          )}
          <Button
            size="sm" className="gap-1.5"
            onClick={() => setShowAddVehicle(true)}
            data-testid="button-add-vehicle"
          >
            <Plus className="w-3.5 h-3.5" />{t("inventory:actions.addVehicle")}
          </Button>
          <Button
            size="sm" variant="outline" className="gap-1.5"
            onClick={() => setShowAlertSettings(true)}
            data-testid="button-alert-settings"
          >
            <Bell className="w-3.5 h-3.5" />{t("inventory:active.alertSettings")}
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      {activeItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:active.unitsOnLot")}</p>
              <p className="text-2xl font-bold tabular-nums" data-testid="stat-units-on-lot">{activeItems.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:active.totalCapital")}</p>
              <p className="text-2xl font-bold tabular-nums" data-testid="stat-total-capital">{fmt$(totalCapital)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:active.totalCostToCarry")}</p>
              <p className="text-2xl font-bold tabular-nums text-destructive" data-testid="stat-total-cost-carry">{fmt$(totalCostToCarry)}</p>
              {carryingCostPerCarPerDay > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{t("inventory:active.perUnitDayAvg", { amount: carryingCostPerCarPerDay.toFixed(2) })}</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:active.ownedInventory")}</p>
              <p className="text-2xl font-bold tabular-nums" data-testid="stat-total-owned">{fmt$(totalOwnedValue)}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("inventory:active.ownedUnitCount", { count: ownedItems.length })}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      {!isLoading && activeItems.length > 0 && (
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("inventory:active.searchPlaceholder")}
            className="pl-8 pr-8 h-9 text-sm"
            data-testid="input-inventory-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              title={t("inventory:active.clearSearch")}
              data-testid="button-clear-search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Inventory table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : activeItems.length === 0 ? (
        <div />
      ) : (
        <div className="rounded-xl border border-border/70 bg-card shadow-sm ring-1 ring-black/[0.02] overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="border-b border-border bg-gradient-to-b from-muted/70 to-muted/40">
                <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 w-14">{t("inventory:active.colYear")}</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">{t("inventory:active.colMake")}</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">{t("inventory:active.colModel")}</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">{t("inventory:active.colVinStock")}</th>
                <th className="text-right px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                  <span title={t("inventory:active.colPurchasePriceTitle")}>{t("inventory:active.colPurchasePrice")}</span>
                </th>
                <th className="text-right px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 w-24">
                  <button
                    onClick={() => toggleSort("days")}
                    className="flex w-full items-center justify-end gap-1 hover:text-foreground transition-colors"
                    title={t("inventory:active.colDaysTitle")}
                    data-testid="sort-days"
                  >
                    {t("inventory:active.colDays")}
                    {sortKey === "days"
                      ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                      : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 w-40">
                  <button
                    onClick={() => toggleSort("funding")}
                    className="flex w-full items-center justify-center gap-1 hover:text-foreground transition-colors"
                    title={t("inventory:active.colFundingTitle")}
                    data-testid="sort-funding"
                  >
                    {t("inventory:active.colFunding")}
                    {sortKey === "funding"
                      ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                      : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="text-right px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                  <span title={t("inventory:active.colCostToCarryTitle")}>{t("inventory:active.colCostToCarry")}</span>
                </th>
                <th className="px-3 py-2.5 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 [&>tr:nth-child(even)]:bg-muted/[0.18]">
              {displayedItems.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-12 text-center text-sm text-muted-foreground" data-testid="text-no-results">
                    {t("inventory:active.noResults", { query: searchQuery })}
                  </td>
                </tr>
              ) : displayedItems.map((item) => (
                <tr key={item.id} className="group hover:bg-primary/[0.04] transition-colors" data-testid={`row-inventory-${item.id}`}>
                  <td className="px-3 py-2.5 text-xs tabular-nums text-muted-foreground border-l-2 border-transparent group-hover:border-primary/60 transition-colors">
                    <IdentityTextCell item={item} field="year" value={item.year} placeholder={t("inventory:identity.placeholderYear")} />
                  </td>
                  <td className="px-3 py-2.5 text-xs font-semibold text-foreground">
                    <IdentityTextCell item={item} field="make" value={item.make} placeholder={t("inventory:identity.placeholderMake")} />
                  </td>
                  <td className="px-3 py-2.5 text-xs text-foreground/90">
                    <IdentityTextCell item={item} field="model" value={item.model} placeholder={t("inventory:identity.placeholderModel")} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-col gap-0.5">
                      <IdentityTextCell item={item} field="vin" value={item.vin} placeholder={t("inventory:identity.placeholderVin")} monospace />
                      {item.stockNumber && <span className="text-[11px] text-muted-foreground">#{item.stockNumber}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <PurchasePriceCell item={item} />
                  </td>
                  <td className="px-3 py-2.5 text-right w-24">
                    <DaysCell item={item} />
                  </td>
                  <td className="px-3 py-2.5 text-center w-40">
                    <FlooredOwnedCell item={item} lenders={lenders} />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <CostToCarryCell item={item} />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm" variant="ghost"
                        className={`h-7 w-7 p-0 ${listedInventoryIds.has(item.id) ? "text-muted-foreground/30 cursor-not-allowed" : "text-muted-foreground/50 hover:text-violet-600 hover:bg-violet-500/10"}`}
                        onClick={() => { if (!listedInventoryIds.has(item.id)) navigate(`/marketplace/new?from=${item.id}`); }}
                        disabled={listedInventoryIds.has(item.id)}
                        data-testid={`button-push-marketplace-${item.id}`}
                        title={listedInventoryIds.has(item.id) ? t("inventory:active.alreadyListed") : t("inventory:active.pushToMarketplace")}
                      >
                        <ShoppingBag className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 text-xs text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10 gap-1 px-2"
                        onClick={() => setSellItem(item)}
                        data-testid={`button-sell-${item.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">{t("inventory:actions.sold")}</span>
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteItem(item)}
                        data-testid={`button-delete-active-${item.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SellDialog item={sellItem} open={sellItem != null} onClose={() => setSellItem(null)} />
      <AlertSettingsDialog open={showAlertSettings} onClose={() => setShowAlertSettings(false)} />
      <InventorySummaryModal
        open={showSummary}
        onClose={() => setShowSummary(false)}
        ownedItems={ownedItems}
        flooredItems={flooredItems}
      />
      <AddVehicleDialog
        open={showAddVehicle}
        onClose={() => setShowAddVehicle(false)}
        lenders={lenders}
        existingVins={activeVinSet}
      />
      <DeleteConfirmDialog item={deleteItem} open={deleteItem != null} onClose={() => setDeleteItem(null)} />
    </div>
  );
}

// ─── Inline Sold Edit Cell ────────────────────────────────────────────────────

type SoldEditField = "purchasePrice" | "salePrice" | "daysInStock" | "costToCarryOverride" | "reconCost";

function InlineSoldEditCell({
  itemId,
  field,
  value,
  isInteger,
  testIdSuffix,
  onSaved,
  onLiveChange,
}: {
  itemId: string;
  field: SoldEditField;
  value: number | null;
  isInteger?: boolean;
  testIdSuffix: string;
  onSaved: () => void;
  onLiveChange?: (val: number | null) => void;
}) {
  const { toast } = useToast();
  const { t } = useTranslation(["inventory", "common"]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const saveMutation = useMutation({
    mutationFn: (val: number | null) =>
      apiRequest("PATCH", `/api/inventory/${itemId}/sold-details`, { [field]: val }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      setEditing(false);
      onLiveChange?.(null);
      onSaved();
    },
    onError: (err: any) => {
      toast({ title: t("inventory:toast.error"), description: err.message, variant: "destructive" });
      onLiveChange?.(null);
    },
  });

  function startEdit() {
    setInputVal(value != null ? String(value) : "");
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function handleChange(raw: string) {
    setInputVal(raw);
    const parsed = isInteger ? parseInt(raw, 10) : parseFloat(raw);
    onLiveChange?.(Number.isFinite(parsed) && parsed >= 0 ? parsed : null);
  }

  function confirm() {
    const trimmed = inputVal.trim();
    if (trimmed === "") {
      if (field === "costToCarryOverride" || field === "reconCost") {
        saveMutation.mutate(null);
      } else {
        setEditing(false);
        onLiveChange?.(null);
      }
      return;
    }
    const num = isInteger ? parseInt(trimmed, 10) : parseFloat(trimmed);
    if (!Number.isFinite(num) || num < 0) {
      toast({ title: t("inventory:toast.invalidValue"), variant: "destructive" });
      return;
    }
    saveMutation.mutate(num);
  }

  function cancelEdit() {
    setEditing(false);
    onLiveChange?.(null);
  }

  if (editing) {
    return (
      <div className="flex items-center justify-end gap-1 relative" onClick={(e) => e.stopPropagation()}>
        {saveMutation.isPending && (
          <span className="text-[10px] text-muted-foreground animate-pulse">{t("inventory:actions.savingInline")}</span>
        )}
        <input
          ref={inputRef}
          type="number"
          min={0}
          step={isInteger ? 1 : 0.01}
          value={inputVal}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={confirm}
          onKeyDown={(e) => {
            if (e.key === "Enter") confirm();
            if (e.key === "Escape") cancelEdit();
          }}
          className="w-24 text-right text-xs border border-primary/40 rounded px-1.5 py-0.5 bg-background outline-none focus:ring-1 focus:ring-primary tabular-nums"
          data-testid={`input-sold-${field}-${testIdSuffix}`}
          autoFocus
        />
      </div>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); startEdit(); }}
      className="text-xs tabular-nums hover:underline cursor-pointer hover:text-primary transition-colors group inline-flex items-center gap-1"
      title={t("inventory:soldRow.clickToEdit")}
      data-testid={`cell-sold-${field}-${testIdSuffix}`}
    >
      {value != null
        ? (isInteger ? `${value}d` : value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }))
        : <span className="text-muted-foreground">—</span>}
    </button>
  );
}

// ─── Sold Unit Row with real-time profit ─────────────────────────────────────

type SoldUnitMetrics = {
  id: string;
  label: string;
  vin: string | null;
  stockNumber: string | null;
  saleDate: string | null;
  fundingType: string | null;
  purchasePrice: number | null;
  salePrice: number;
  days: number;
  costToCarry: number;
  costToCarryOverride: string | null;
  reconCost: string | null;
  avgReconCost: number;
  profit: number | null;
  profitPerDay: number | null;
  item: EnrichedInventoryItem;
};

function SoldUnitRow({
  unit,
  expanded,
  onToggleExpand,
  onDelete,
}: {
  unit: SoldUnitMetrics;
  expanded: boolean;
  onToggleExpand: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation(["inventory", "common"]);
  const [draftPurchase, setDraftPurchase] = useState<number | null>(null);
  const [draftSale, setDraftSale] = useState<number | null>(null);
  const [draftCost, setDraftCost] = useState<number | null>(null);
  const [draftRecon, setDraftRecon] = useState<number | null>(null);

  const livePurchase = draftPurchase ?? unit.purchasePrice;
  const liveSale = draftSale ?? unit.salePrice;
  const liveCost = draftCost ?? unit.costToCarry;
  const effectiveRecon = unit.reconCost != null ? parseFloat(unit.reconCost) : unit.avgReconCost;
  const liveRecon = draftRecon ?? effectiveRecon;

  const liveProfit = livePurchase != null ? liveSale - livePurchase - liveCost - liveRecon : null;

  return (
    <Fragment>
      <tr
        className="hover:bg-muted/20 transition-colors cursor-pointer"
        onClick={onToggleExpand}
        data-testid={`row-sold-${unit.id}`}
      >
        <td className="px-3 py-2.5 text-muted-foreground">
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </td>
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <IdentityTextCell item={unit.item} field="year" value={unit.item.year} placeholder={t("inventory:identity.placeholderYear")} />
        </td>
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <IdentityTextCell item={unit.item} field="make" value={unit.item.make} placeholder={t("inventory:identity.placeholderMake")} />
        </td>
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col gap-0.5">
            <IdentityTextCell item={unit.item} field="model" value={unit.item.model} placeholder={t("inventory:identity.placeholderModel")} />
            <IdentityTextCell item={unit.item} field="vin" value={unit.item.vin} placeholder={t("inventory:identity.placeholderVin")} monospace />
          </div>
        </td>
        <td className="px-3 py-2.5 text-right">
          <InlineSoldEditCell
            itemId={unit.id}
            field="purchasePrice"
            value={unit.purchasePrice}
            testIdSuffix={unit.id}
            onSaved={() => setDraftPurchase(null)}
            onLiveChange={setDraftPurchase}
          />
        </td>
        <td className="px-3 py-2.5 text-right font-medium">
          <InlineSoldEditCell
            itemId={unit.id}
            field="salePrice"
            value={unit.salePrice}
            testIdSuffix={unit.id}
            onSaved={() => setDraftSale(null)}
            onLiveChange={setDraftSale}
          />
        </td>
        <td className="px-3 py-2.5 text-right">
          <InlineSoldEditCell
            itemId={unit.id}
            field="daysInStock"
            value={unit.days > 0 ? unit.days : null}
            isInteger
            testIdSuffix={unit.id}
            onSaved={() => {}}
          />
        </td>
        <td className="px-3 py-2.5 text-right text-destructive/80">
          <InlineSoldEditCell
            itemId={unit.id}
            field="costToCarryOverride"
            value={unit.costToCarryOverride != null ? parseFloat(unit.costToCarryOverride) : unit.costToCarry}
            testIdSuffix={unit.id}
            onSaved={() => setDraftCost(null)}
            onLiveChange={setDraftCost}
          />
          {unit.costToCarryOverride == null && draftCost == null && (
            <span className="text-[10px] text-muted-foreground/60 ml-0.5">{t("inventory:soldRow.calc")}</span>
          )}
        </td>
        <td className="px-3 py-2.5 text-right text-destructive/80">
          <InlineSoldEditCell
            itemId={unit.id}
            field="reconCost"
            value={effectiveRecon}
            testIdSuffix={unit.id}
            onSaved={() => setDraftRecon(null)}
            onLiveChange={setDraftRecon}
          />
          {unit.reconCost == null && draftRecon == null && (
            <span className="text-[10px] text-muted-foreground/60 ml-0.5">{t("inventory:soldRow.auto")}</span>
          )}
        </td>
        <td
          className={`px-3 py-2.5 text-right text-xs font-semibold tabular-nums ${getProfitColor(liveProfit ?? 0)}`}
          data-testid={`text-profit-${unit.id}`}
        >
          {liveProfit != null ? fmt$(liveProfit) : "—"}
        </td>
        <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
          {onDelete && (
            <Button
              size="sm" variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10"
              onClick={onDelete}
              data-testid={`button-delete-sold-${unit.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr key={`${unit.id}-expanded`} className="bg-muted/20">
          <td />
          <td colSpan={10} className="px-3 py-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-muted-foreground">{t("inventory:soldRow.saleDate")}</p>
                <InlineSoldDateCell
                  itemId={unit.id}
                  value={unit.saleDate}
                  onSaved={() => {}}
                  queryKeys={[["/api/inventory/enriched"]]}
                  testIdPrefix="sold"
                />
              </div>
              <div>
                <p className="text-muted-foreground">{t("inventory:soldRow.stockNumber")}</p>
                <p className="font-medium">{unit.stockNumber ?? "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t("inventory:soldRow.profitPerDay")}</p>
                <p className={`font-medium ${getProfitColor(unit.profitPerDay)}`}>
                  {unit.profitPerDay !== null ? t("inventory:soldRow.perDay", { amount: fmt$(unit.profitPerDay, 2) }) : "—"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">{t("inventory:soldRow.funding")}</p>
                <p className="font-medium capitalize">{unit.fundingType ?? "—"}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// ─── Inventory Analysis Tab (sold performance) ────────────────────────────────

const CHART_COLORS = [
  "hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
  "hsl(var(--chart-4))", "hsl(var(--chart-5))",
];

const PRICE_BUCKETS = [
  { label: "$0–5k",   min: 0,     max: 5000 },
  { label: "$5–10k",  min: 5000,  max: 10000 },
  { label: "$10–15k", min: 10000, max: 15000 },
  { label: "$15–20k", min: 15000, max: 20000 },
  { label: "$20–25k", min: 20000, max: 25000 },
  { label: "$25–30k", min: 25000, max: 30000 },
  { label: "$30k+",   min: 30000, max: Infinity },
];

function InventoryAnalysisTab({ items, avgReconCost }: { items: EnrichedInventoryItem[]; avgReconCost: number }) {
  const { t } = useTranslation(["inventory", "common"]);
  const [activeChart, setActiveChart] = useState<"profit-day-model" | "profit-day-year" | "profit-day-make" | "profit-day-price">("profit-day-model");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteItem, setDeleteItem] = useState<EnrichedInventoryItem | null>(null);

  const soldItems = useMemo(() => items.filter((i) => i.status === "sold"), [items]);

  const [soldUnitsCollapsed, setSoldUnitsCollapsed] = useState(false);
  const [hasUserToggled, setHasUserToggled] = useState(false);

  useEffect(() => {
    if (!hasUserToggled && soldItems.length > 0) {
      setSoldUnitsCollapsed(soldItems.length > 5);
    }
  }, [soldItems.length, hasUserToggled]);

  const soldWithMetrics = useMemo(() =>
    soldItems
      .filter((i) => i.salePrice)
      .map((i) => {
        const salePrice = parseFloat(i.salePrice!);
        const purchasePrice = i.purchasePrice ? parseFloat(i.purchasePrice) : null;
        // Prefer the stored daysInStock, but fall back to the server-derived daysOnLot
        // (computed from purchase → sale dates) so sold units missing daysInStock still
        // get a valid days value and aren't silently dropped from the profit/day charts.
        const days = (i.daysInStock && i.daysInStock > 0) ? i.daysInStock : (i.daysOnLot ?? 0);
        const effectiveRecon = i.reconCost != null ? parseFloat(i.reconCost) : avgReconCost;
        const profit = purchasePrice != null ? salePrice - purchasePrice - i.costToCarry - effectiveRecon : null;
        const profitPerDay = profit != null && days > 0 ? profit / days : null;
        const label = [i.year, i.make, i.model].filter(Boolean).join(" ") || t("inventory:summary.unknown");
        return { ...i, salePrice, purchasePrice, days, reconCost: i.reconCost, avgReconCost, profit, profitPerDay, label, item: i };
      })
      .sort((a, b) => (b.saleDate ?? "").localeCompare(a.saleDate ?? "")),
    [soldItems, avgReconCost]
  );

  // Chart data
  const profitPerDayMakeData = useMemo(() => {
    const byMake = new Map<string, number[]>();
    for (const u of soldWithMetrics) {
      if (!u.make || u.profitPerDay === null) continue;
      if (!byMake.has(u.make)) byMake.set(u.make, []);
      byMake.get(u.make)!.push(u.profitPerDay);
    }
    return Array.from(byMake.entries())
      .map(([name, vals]) => ({ name, profitPerDay: vals.reduce((s, v) => s + v, 0) / vals.length, count: vals.length }))
      .sort((a, b) => b.profitPerDay - a.profitPerDay);
  }, [soldWithMetrics]);

  const profitByPriceData = useMemo(() =>
    PRICE_BUCKETS.map((b) => {
      const rows = soldWithMetrics.filter((u) => u.purchasePrice != null && u.purchasePrice >= b.min && u.purchasePrice < b.max && u.profitPerDay !== null);
      return {
        name: b.label,
        profitPerDay: rows.length > 0 ? rows.reduce((s, r) => s + r.profitPerDay!, 0) / rows.length : null,
        count: rows.length,
      };
    }).filter((b) => b.count > 0),
    [soldWithMetrics]
  );

  const profitPerDayModelData = useMemo(() => {
    const byModel = new Map<string, number[]>();
    for (const u of soldWithMetrics) {
      if (!u.model || u.profitPerDay === null) continue;
      if (!byModel.has(u.model)) byModel.set(u.model, []);
      byModel.get(u.model)!.push(u.profitPerDay);
    }
    return Array.from(byModel.entries())
      .map(([name, vals]) => ({ name, profitPerDay: vals.reduce((s, v) => s + v, 0) / vals.length, count: vals.length }))
      .sort((a, b) => b.profitPerDay - a.profitPerDay);
  }, [soldWithMetrics]);

  const profitPerDayYearData = useMemo(() => {
    const byYear = new Map<string, number[]>();
    for (const u of soldWithMetrics) {
      if (!u.year || u.profitPerDay === null) continue;
      const yr = String(u.year);
      if (!byYear.has(yr)) byYear.set(yr, []);
      byYear.get(yr)!.push(u.profitPerDay);
    }
    return Array.from(byYear.entries())
      .map(([name, vals]) => ({ name, profitPerDay: vals.reduce((s, v) => s + v, 0) / vals.length, count: vals.length }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [soldWithMetrics]);

  // Chart-only subsets: same data as above but with negative avg profit/day entries removed
  // so bars never extend below the $0 baseline. Leaderboard rows use the full arrays above.
  const chartModelData = useMemo(() => profitPerDayModelData.filter((e) => e.profitPerDay >= 0), [profitPerDayModelData]);
  const chartYearData = useMemo(() => profitPerDayYearData.filter((e) => e.profitPerDay >= 0), [profitPerDayYearData]);
  const chartMakeData = useMemo(() => profitPerDayMakeData.filter((e) => e.profitPerDay >= 0), [profitPerDayMakeData]);
  const chartPriceData = useMemo(() => profitByPriceData.filter((b) => (b.profitPerDay ?? 0) >= 0), [profitByPriceData]);

  // Summary stats
  const totalRevenue = useMemo(() => soldWithMetrics.reduce((s, u) => s + u.salePrice, 0), [soldWithMetrics]);
  const totalProfit = useMemo(() => soldWithMetrics.reduce((s, u) => s + (u.profit ?? 0), 0), [soldWithMetrics]);
  const avgDays = useMemo(() =>
    soldWithMetrics.length > 0 ? soldWithMetrics.reduce((s, u) => s + u.days, 0) / soldWithMetrics.length : 0,
    [soldWithMetrics]
  );

  const chartOptions = [
    { key: "profit-day-model" as const, label: t("inventory:analysis.chartModel") },
    { key: "profit-day-year" as const, label: t("inventory:analysis.chartYear") },
    { key: "profit-day-make" as const, label: t("inventory:analysis.chartMake") },
    { key: "profit-day-price" as const, label: t("inventory:analysis.chartPrice") },
  ];

  if (soldItems.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <TrendingUp className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
          <h3 className="font-semibold text-sm mb-1">{t("inventory:analysis.noSoldTitle")}</h3>
          <p className="text-xs text-muted-foreground max-w-xs mx-auto">
            {t("inventory:analysis.noSoldDescription")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:analysis.unitsSold")}</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="stat-units-sold">{soldItems.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:analysis.avgTurnTime")}</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="stat-avg-turn">{fmtNum(avgDays, 0)}d</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:analysis.totalRevenue")}</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="stat-total-revenue">{fmt$(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:analysis.totalNetProfit")}</p>
            <p className={`text-2xl font-bold tabular-nums ${getProfitColor(totalProfit)}`} data-testid="stat-total-profit">{fmt$(totalProfit)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Sold units expandable list */}
      <Card>
        <CardHeader
          className="pb-3 cursor-pointer select-none"
          onClick={() => { setHasUserToggled(true); setSoldUnitsCollapsed((c) => !c); }}
          data-testid="button-toggle-sold-units"
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-sm">
                {t("inventory:analysis.soldUnitsTitle", { count: soldWithMetrics.length })}
              </CardTitle>
              <CardDescription className="text-xs">{t("inventory:analysis.soldUnitsDescription")}</CardDescription>
            </div>
            {soldUnitsCollapsed
              ? <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            }
          </div>
        </CardHeader>
        {!soldUnitsCollapsed && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground w-6" />
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground w-14">{t("inventory:analysis.colYear")}</th>
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colMake")}</th>
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colModel")}</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colPurchase")}</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colSale")}</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colDays")}</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colCostToCarry")}</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colReconCost")}</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:analysis.colProfitLoss")}</th>
                    <th className="px-2 py-2.5 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {soldWithMetrics.map((unit) => (
                    <SoldUnitRow
                      key={unit.id}
                      unit={unit}
                      expanded={expandedId === unit.id}
                      onToggleExpand={() => setExpandedId(expandedId === unit.id ? null : unit.id)}
                      onDelete={() => setDeleteItem(unit.item)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Charts */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-sm">{t("inventory:analysis.performanceCharts")}</CardTitle>
              <CardDescription className="text-xs mt-0.5">{t("inventory:analysis.performanceChartsDesc")}</CardDescription>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {chartOptions.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveChart(key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${activeChart === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                  data-testid={`button-chart-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Profit per Day by Model */}
          {activeChart === "profit-day-model" && (
            profitPerDayModelData.length === 0 ? (
              <div className="h-44 flex items-center justify-center"><p className="text-sm text-muted-foreground">{t("inventory:analysis.noModelData")}</p></div>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartModelData} margin={{ top: 8, right: 16, left: 8, bottom: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} height={36} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toFixed(0)}`} width={60} domain={[0, 'auto']} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} formatter={(v: number, _: string, props: any) => [t("inventory:analysis.tooltipPerDay", { amount: fmt$(v, 2), count: props.payload.count }), t("inventory:analysis.avgProfitDay")]} />
                    <Bar dataKey="profitPerDay" name={t("inventory:analysis.avgProfitDay")} radius={[4, 4, 0, 0]}>
                      {chartModelData.map((entry, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2" data-testid="leaderboard-model">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("inventory:analysis.topModels")}</p>
                  {profitPerDayModelData.slice(0, 3).map((entry, i) => (
                    <div key={entry.name} className="flex items-center gap-3 px-3 py-2 rounded-md bg-muted/40" data-testid={`leaderboard-model-${i + 1}`}>
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${i === 0 ? "bg-yellow-400/20 text-yellow-600 dark:text-yellow-400" : i === 1 ? "bg-slate-300/20 text-slate-500 dark:text-slate-400" : "bg-orange-400/20 text-orange-600 dark:text-orange-400"}`}>
                        {i + 1}
                      </span>
                      <span className="flex-1 text-sm font-medium truncate">{entry.name}</span>
                      <span className="text-sm font-semibold tabular-nums">{t("inventory:analysis.perDay", { amount: fmt$(entry.profitPerDay, 2) })}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{t("inventory:analysis.soldCount", { count: entry.count })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}

          {/* Profit per Day by Year */}
          {activeChart === "profit-day-year" && (
            profitPerDayYearData.length === 0 ? (
              <div className="h-44 flex items-center justify-center"><p className="text-sm text-muted-foreground">{t("inventory:analysis.noYearData")}</p></div>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartYearData} margin={{ top: 8, right: 16, left: 8, bottom: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} height={36} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toFixed(0)}`} width={60} domain={[0, 'auto']} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} formatter={(v: number, _: string, props: any) => [t("inventory:analysis.tooltipPerDay", { amount: fmt$(v, 2), count: props.payload.count }), t("inventory:analysis.avgProfitDay")]} />
                    <Bar dataKey="profitPerDay" name={t("inventory:analysis.avgProfitDay")} radius={[4, 4, 0, 0]}>
                      {chartYearData.map((entry, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2" data-testid="leaderboard-year">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("inventory:analysis.topYears")}</p>
                  {[...profitPerDayYearData].sort((a, b) => b.profitPerDay - a.profitPerDay).slice(0, 3).map((entry, i) => (
                    <div key={entry.name} className="flex items-center gap-3 px-3 py-2 rounded-md bg-muted/40" data-testid={`leaderboard-year-${i + 1}`}>
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${i === 0 ? "bg-yellow-400/20 text-yellow-600 dark:text-yellow-400" : i === 1 ? "bg-slate-300/20 text-slate-500 dark:text-slate-400" : "bg-orange-400/20 text-orange-600 dark:text-orange-400"}`}>
                        {i + 1}
                      </span>
                      <span className="flex-1 text-sm font-medium truncate">{entry.name}</span>
                      <span className="text-sm font-semibold tabular-nums">{t("inventory:analysis.perDay", { amount: fmt$(entry.profitPerDay, 2) })}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{t("inventory:analysis.soldCount", { count: entry.count })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}

          {/* Profit per Day by Make */}
          {activeChart === "profit-day-make" && (
            profitPerDayMakeData.length === 0 ? (
              <div className="h-44 flex items-center justify-center"><p className="text-sm text-muted-foreground">{t("inventory:analysis.noMakeData")}</p></div>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartMakeData} margin={{ top: 8, right: 16, left: 8, bottom: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} height={36} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toFixed(0)}`} width={60} domain={[0, 'auto']} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} formatter={(v: number, _: string, props: any) => [t("inventory:analysis.tooltipPerDay", { amount: fmt$(v, 2), count: props.payload.count }), t("inventory:analysis.avgProfitDay")]} />
                    <Bar dataKey="profitPerDay" name={t("inventory:analysis.avgProfitDay")} radius={[4, 4, 0, 0]}>
                      {chartMakeData.map((entry, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2" data-testid="leaderboard-make">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("inventory:analysis.topMakes")}</p>
                  {[...profitPerDayMakeData].sort((a, b) => b.profitPerDay - a.profitPerDay).slice(0, 3).map((entry, i) => (
                    <div key={entry.name} className="flex items-center gap-3 px-3 py-2 rounded-md bg-muted/40" data-testid={`leaderboard-make-${i + 1}`}>
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${i === 0 ? "bg-yellow-400/20 text-yellow-600 dark:text-yellow-400" : i === 1 ? "bg-slate-300/20 text-slate-500 dark:text-slate-400" : "bg-orange-400/20 text-orange-600 dark:text-orange-400"}`}>
                        {i + 1}
                      </span>
                      <span className="flex-1 text-sm font-medium truncate">{entry.name}</span>
                      <span className="text-sm font-semibold tabular-nums">{t("inventory:analysis.perDay", { amount: fmt$(entry.profitPerDay, 2) })}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{t("inventory:analysis.soldCount", { count: entry.count })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}

          {/* Profit per Day by Price Range */}
          {activeChart === "profit-day-price" && (
            profitByPriceData.length === 0 ? (
              <div className="h-44 flex items-center justify-center"><p className="text-sm text-muted-foreground">{t("inventory:analysis.noPriceData")}</p></div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartPriceData} margin={{ top: 8, right: 16, left: 8, bottom: 32 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} height={36} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toFixed(0)}`} width={60} domain={[0, 'auto']} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} formatter={(v: number, _: string, props: any) => [t("inventory:analysis.tooltipPerDay", { amount: fmt$(v, 2), count: props.payload.count }), t("inventory:analysis.avgProfitDay")]} />
                  <Bar dataKey="profitPerDay" name={t("inventory:analysis.avgProfitDay")} radius={[4, 4, 0, 0]}>
                    {chartPriceData.map((entry, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )
          )}
        </CardContent>
      </Card>
      <DeleteConfirmDialog item={deleteItem} open={deleteItem != null} onClose={() => setDeleteItem(null)} />
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const { t } = useTranslation(["inventory", "common"]);
  const { data: authUser } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const { data, isLoading } = useQuery<EnrichedResponse>({ queryKey: ["/api/inventory/enriched"] });

  const items = data?.items ?? [];
  const lenders = data?.lenders ?? [];
  const carryingCostPerCarPerDay = data?.carryingCostPerCarPerDay ?? 0;
  const avgReconCost = data?.avgReconCost ?? 0;

  const activeItems = useMemo(() => items.filter((i) => i.status === "active"), [items]);
  const soldItems = useMemo(() => items.filter((i) => i.status === "sold"), [items]);

  if (authUser?.memberPermissions && !authUser.memberPermissions.inventoryAccess) {
    return <AccessRestricted feature={t("inventory:page.accessFeature")} />;
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-screen-xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("inventory:page.title")}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{t("inventory:page.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Car className="w-4 h-4" />
            <span data-testid="header-active-count">{t("inventory:page.activeCount", { count: activeItems.length })}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-4 h-4" />
            <span data-testid="header-sold-count">{t("inventory:page.soldCount", { count: soldItems.length })}</span>
          </div>
          {carryingCostPerCarPerDay > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <Clock className="w-3.5 h-3.5" />
              <span>{t("inventory:page.carryCost", { amount: carryingCostPerCarPerDay.toFixed(2) })}</span>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active-inventory">
            {t("inventory:page.tabActive", { count: activeItems.length })}
          </TabsTrigger>
          <TabsTrigger value="analysis" data-testid="tab-inventory-analysis">
            {t("inventory:page.tabAnalysis", { count: soldItems.length })}
          </TabsTrigger>
          <TabsTrigger value="aged" data-testid="tab-aged-inventory">
            {t("inventory:page.tabAged")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          <ActiveInventoryTab
            items={items}
            lenders={lenders}
            carryingCostPerCarPerDay={carryingCostPerCarPerDay}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="analysis" className="mt-4">
          <InventoryAnalysisTab items={items} avgReconCost={avgReconCost} />
        </TabsContent>

        <TabsContent value="aged" className="mt-4">
          <AgedInventorySection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
